/**
 * Static catalog for the Growth Stages feature matrix.
 *
 * The offering catalog is not modeled in the database; it is a content
 * snapshot owned by product and edited via PR. Column marketing copy is
 * keyed by stage position (1-4) so the "YOU ARE HERE" marker can match on
 * the member company's `companyStage.position` regardless of how the DB
 * stage catalog is named.
 *
 * Source of truth: docs/features/stage-management/06-member-stage-feature-matrix/spec.md
 * (Content Inventory). The header totals are computed from this data, never
 * hardcoded: cumulative stage totals resolve to 13 / 27 / 39 / 49 and there
 * are 19 a la carte items.
 */

export type StagePosition = 1 | 2 | 3 | 4;

export type CellState = "not-included" | "new" | "carried";

export interface StageColumn {
  position: StagePosition;
  label: string;
  persona: string;
  businessSize: string;
  /** Per-stage accent hue (hex), applied on top of theme-following surfaces. */
  accent: string;
}

export interface Offering {
  name: string;
  description: string;
  tag: string;
  /** First stage that includes the offering; null for a la carte-only items. */
  entryStagePosition: StagePosition | null;
  alaCarte: boolean;
}

export interface OfferingCategory {
  name: string;
  tagline: string;
  /** Category accent hue (hex) for the gradient header bar and swatch. */
  accent: string;
  offerings: Offering[];
}

export const stageColumns: StageColumn[] = [
  {
    position: 1,
    label: "Build",
    persona: "The Member",
    businessSize: "All Members",
    accent: "#34D399",
  },
  {
    position: 2,
    label: "Grow",
    persona: "The Operator",
    businessSize: "4-10 People",
    accent: "#60A5FA",
  },
  {
    position: 3,
    label: "Lead",
    persona: "The Leader",
    businessSize: "10-30 People",
    accent: "#A78BFA",
  },
  {
    position: 4,
    label: "Scale",
    persona: "The CEO",
    businessSize: "30+ People",
    accent: "#FBBF24",
  },
];

export const offeringCatalog: OfferingCategory[] = [
  {
    name: "Coaching",
    tagline:
      "1-on-1 and group coaching from CertainPath's focused, passionate experts.",
    accent: "#60A5FA",
    offerings: [
      {
        name: "Coach Connect (Field Focus)",
        description: "Frontline-topic coaching included with your membership.",
        tag: "INSPECT",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "The Accountability Group (TAG) - Stage-Based",
        description: "2 hours of group accountability per month/year.",
        tag: "INSPECT",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Specialty Coaching",
        description: "Accounting and marketing specialists on call.",
        tag: "FINANCIALS",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Coach Connect (Management Focus)",
        description: "Management-topic coaching included with your membership.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Strategic Operation Action Review (SOAR)",
        description:
          "Deep-dive strategic review of your operation with a CertainPath advisor.",
        tag: "INSPECT",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
  {
    name: "Community",
    tagline: "Events, podcasts, and gatherings where you meet your peers.",
    accent: "#34D399",
    offerings: [
      {
        name: "Spring Expo Attendance",
        description: "Annual member conference. Complimentary for members.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Spring Expo Gala Table (Included)",
        description: "Premier gala table at Spring Expo.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "Fall Expo Attendance",
        description: "Fall member gathering. Complimentary for members.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Eagles Attendance",
        description:
          "The conference for top operators. Complimentary for members.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Dashboard",
    tagline: "The numbers your operating system needs to surface, and how.",
    accent: "#FB923C",
    offerings: [
      {
        name: "Total Sales New Versus Existing Customer (MEER)",
        description: "",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Total Sales by Year & Month (MEER)",
        description: "",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Revenue by Invoice Age (MEER)",
        description: "",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Average Invoice Comparison (MEER)",
        description: "",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Total Sales by ZIP Code (MEER)",
        description: "",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Total Sales New Customer by ZIP Code & Year (MEER)",
        description: "",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Percentage of New Customers by ZIP Code (MEER)",
        description: "",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Total Households by ZIP Code (MEER)",
        description: "",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "New Customer Replacement Efficiency (MEER)",
        description: "",
        tag: "REPORTS",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Existing Customer Replacement Efficiency (MEER)",
        description: "",
        tag: "REPORTS",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Lifetime Value by Tier (MEER)",
        description: "",
        tag: "MARKETING",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "Total Addressable Market (MEER)",
        description: "",
        tag: "MARKETING",
        entryStagePosition: 4,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Group Buying",
    tagline: "Negotiated pricing and rebates across the partner network.",
    accent: "#F472B6",
    offerings: [
      {
        name: "Partner Network",
        description:
          "Group buying, company-specific rebates from CertainPath partners.",
        tag: "INVENTORY",
        entryStagePosition: 1,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Marketing",
    tagline: "Done-for-you demand generation to keep your pipeline full.",
    accent: "#F87171",
    offerings: [
      {
        name: "CertainPath Lead Generation",
        description: "Done-for-you lead generation to keep your pipeline full.",
        tag: "MARKETING",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
  {
    name: "Software",
    tagline: "The CertainPath operating platform.",
    accent: "#22D3EE",
    offerings: [
      {
        name: "CertainPath Software",
        description:
          "The operating platform: dispatch, customers, jobs, invoices, and calls.",
        tag: "DISPATCH",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
  {
    name: "Trademark",
    tagline:
      "Brand seals that signal to homeowners you've earned the right to be in their home.",
    accent: "#FBBF24",
    offerings: [
      {
        name: "CertainPath USPs",
        description: "Brand seals and USPs homeowners recognize and trust.",
        tag: "MARKETING",
        entryStagePosition: 1,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Training",
    tagline:
      "Classroom, virtual, and onsite training that turns knowledge into shop-floor change.",
    accent: "#A78BFA",
    offerings: [
      {
        name: "Executive Perspective",
        description: "Onsite walkthrough of where your business stands today.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "HUB Resource Library",
        description: "Searchable library of resources, organized by stage.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Service Essentials",
        description:
          "Classroom/VILT sales course for service techs. $1,299 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Telephone Essentials",
        description: "CSR call-handling fundamentals. $798 per tech.",
        tag: "CALL INTAKE",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Dispatching for Profits",
        description: "Dispatch playbook, classroom format. $798 per tech.",
        tag: "DISPATCH",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Impact Workshops",
        description:
          "Hands-on workshops that turn training into shop-floor change.",
        tag: "INSPECT",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Electricians Advanced",
        description: "Advanced electrical sales training. $1,449 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "HVAC Sales",
        description: "Sales training for HVAC service techs. $1,499 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Plumbers Advanced",
        description: "Advanced plumbing sales training. $1,449 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Roofing Specialist Sales Training",
        description: "$1,499 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Fundamentals of Drain Cleaning",
        description: "Onsite technical training. $999 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Advanced Selling",
        description: "Classroom sales course for closers. $1,799 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Foundational Leadership",
        description: "Classroom/VILT leadership fundamentals. $1,499 per tech.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Emotional Intelligence",
        description: "Leadership EQ course. $1,499 per tech.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Call Center Management",
        description: "Management training for call-center leaders.",
        tag: "CALL INTAKE",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Developing Effective Managers",
        description: "Classroom/VILT manager development. $1,799 per tech.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Planning for Success",
        description:
          "Classroom course on operational planning. $1,799 per tech.",
        tag: "DISPATCH",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Recruit and Retain Top Talent",
        description: "Classroom hiring and retention. $1,499 per tech.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Sales Management",
        description: "Classroom course for sales managers. $1,799 per tech.",
        tag: "RUN CALL",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Service Management",
        description:
          "Classroom service-department management. $1,799 per tech.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "The Lion's Pride, Group",
        description: "Leadership group coaching. $499/mo per company.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Call Center Mastery Group",
        description: "Weekly meetings, per tech. $399/mo.",
        tag: "CALL INTAKE",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "Sales Management Mastery Group",
        description: "Weekly meetings, per tech. $399/mo.",
        tag: "RUN CALL",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "The Lion's Pride, Individual",
        description: "Individual leadership meetings + onsite. $999/mo.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "RevenueAscent Electrical/Plumbing/Roofing",
        description: "Revenue program. $3,500/mo.",
        tag: "RUN CALL",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "RevenueAscent HVAC",
        description: "Full HVAC revenue program. $4,500/mo.",
        tag: "RUN CALL",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "Developing Winning Trainers",
        description: "Train your trainers. $1,799 per tech.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: true,
      },
      {
        name: "Field Coach",
        description: "Virtual coaching for techs. $150/mo per tech.",
        tag: "RUN CALL",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
];

/**
 * Resolves how an offering should render in a given stage column:
 * "not-included" before its entry stage (or for a la carte-only items),
 * "new" at its entry stage, "carried" for every stage after entry.
 */
export function cellState(
  offering: Offering,
  stagePosition: StagePosition,
): CellState {
  const entry = offering.entryStagePosition;
  if (entry === null || stagePosition < entry) {
    return "not-included";
  }
  if (stagePosition === entry) {
    return "new";
  }
  return "carried";
}

/**
 * Cumulative offering counts per stage position (index 0 = position 1).
 * A stage's total is everything new at that stage plus everything carried
 * forward. Resolves to [13, 27, 39, 49] for the current catalog.
 */
export function stageTotals(
  catalog: OfferingCategory[] = offeringCatalog,
): number[] {
  return stageColumns.map(
    (column) =>
      catalog
        .flatMap((category) => category.offerings)
        .filter(
          (offering) =>
            offering.entryStagePosition !== null &&
            offering.entryStagePosition <= column.position,
        ).length,
  );
}

/**
 * Total number of a la carte offerings. Resolves to 19 for the current
 * catalog.
 */
export function alaCarteTotal(
  catalog: OfferingCategory[] = offeringCatalog,
): number {
  return catalog
    .flatMap((category) => category.offerings)
    .filter((offering) => offering.alaCarte).length;
}
